import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNotifications } from '@decisiv/ui-components';

import {
  removeAccountAccess,
  restoreAccountAccess,
} from '../services/accountAccessService';

const NOTIFICATION_DURATION = 8000;

export const REMOVED_MESSAGE = 'Account access removed.';
export const RESTORED_MESSAGE = 'Account access restored.';
const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorMessage = (errors) => (errors.base || []).join(' ') || GENERIC_ERROR;

function useAccountAccess(userId, accountAccess) {
  const [accounts, setAccounts] = useState(accountAccess);

  useEffect(() => {
    setAccounts(accountAccess);
  }, [accountAccess]);

  const [pendingAccount, setPendingAccount] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalError, setModalError] = useState(null);
  const [isTerminal, setIsTerminal] = useState(false);

  const { notify } = useNotifications();

  const activeCount = useMemo(
    () => accounts.filter((row) => !row.removedAt).length,
    [accounts],
  );

  const canActOn = useCallback(
    (row) => Boolean(row.removedAt) || activeCount > 1,
    [activeCount],
  );

  const openRemoveModal = useCallback((account) => {
    setPendingAccount(account);
    setPendingAction('remove');
    setModalError(null);
    setIsTerminal(false);
  }, []);

  const openRestoreModal = useCallback((account) => {
    setPendingAccount(account);
    setPendingAction('restore');
    setModalError(null);
    setIsTerminal(false);
  }, []);

  const closeModal = useCallback(() => {
    setPendingAccount(null);
    setPendingAction(null);
    setModalError(null);
    setIsTerminal(false);
  }, []);

  const confirm = useCallback(async () => {
    if (!pendingAccount || !pendingAction) return;

    setIsSubmitting(true);
    setModalError(null);
    setIsTerminal(false);

    try {
      const act =
        pendingAction === 'remove' ? removeAccountAccess : restoreAccountAccess;
      const result = await act(userId, pendingAccount.id);

      if (result.errors) {
        setModalError(errorMessage(result.errors));
        setIsTerminal(Boolean(result.terminal));

        return;
      }

      setAccounts(result.accountAccess);
      notify({
        intent: 'success',
        title: pendingAction === 'remove' ? REMOVED_MESSAGE : RESTORED_MESSAGE,
        duration: NOTIFICATION_DURATION,
        onClose: () => {},
      });
      closeModal();
    } catch (error) {
      setModalError(GENERIC_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }, [closeModal, notify, pendingAction, pendingAccount, userId]);

  return {
    canActOn,
    closeModal,
    confirm,
    isSubmitting,
    isTerminal,
    modalError,
    openRemoveModal,
    openRestoreModal,
    pendingAction,
    pendingAccount,
    accounts,
    setAccounts,
  };
}

export default useAccountAccess;
